/**
 * Turning a sync name and a passphrase into an identity, without telling a relay
 * either one. FR-501, S-07, and the promise the consent screen already makes.
 *
 * ## What this has to achieve
 *
 * A second device must reach the same account from remembered values alone —
 * nothing transferred, nothing scanned, nothing recoverable by anyone else. Gun
 * already provides that shape: `user.create(alias, pass)` generates a random SEA
 * keypair, encrypts it under `pass`, and stores it at `~@<alias>`; `user.auth`
 * fetches and decrypts it. Two devices, same two secrets, same identity.
 *
 * Used directly it is unacceptable here, for one reason: `~@<alias>` is a public
 * node, so the alias is published in the clear. A sync name is the kind of thing
 * people pick from their own life, and a relay that can enumerate aliases can tell
 * who has an account on a menstrual tracker. That is the threat model's A3 and
 * A7 handed a list.
 *
 * So the alias is blinded. The relay sees 32 opaque bytes and an encrypted blob it
 * cannot attribute, and the app never sends the name or the passphrase anywhere.
 *
 * ## What is derived, and why each is separate
 *
 * One PBKDF2 pass produces a master secret; HKDF splits it by purpose:
 *
 * | Output | Purpose | Why not shared |
 * | --- | --- | --- |
 * | alias | the public `~@<alias>` node | public — must reveal nothing about the others |
 * | SEA password | encrypts the keypair Gun stores on the relay | leaves the device; must not be the passphrase itself |
 * | sync key material | seals records, and names souls via `deriveSoulKey` | never leaves the device at all |
 *
 * The SEA password matters most. Passing the user's actual passphrase to
 * `gunRegisterAndLogin` would hand the relay a blob encrypted under the very
 * secret that also names their souls and unseals their entries — one offline
 * success would take everything. Deriving a distinct value means the blob's
 * compromise costs the blob.
 *
 * ## The cost of this design, stated plainly
 *
 * The encrypted keypair does sit on the relay, and SEA chooses its own wrapping
 * parameters. A relay operator can copy that blob and attack it offline at
 * whatever cost SEA picked, which this module does not control. The alternative —
 * deriving the keypair deterministically and storing nothing — is stronger, and
 * was rejected because it needs a hand-written P-256 derivation and two new crypto
 * dependencies in a project that has none and no security review. That trade is
 * recorded in the roadmap; it should be revisited if a review ever happens.
 *
 * This is why the passphrase strength meter is not decoration. `secretStrength`
 * gates it, and the copy already says a short one is attackable offline.
 */
import { fromBase64, toBase64 } from './crypto'

/**
 * Bound to the derivation, not to the app. Changing any of these changes every
 * identity — the same name and passphrase would reach a different account, and
 * the old one would look like it had simply vanished. A change here is a
 * migration, not a tweak.
 */
const PBKDF2_ITERATIONS = 600_000
const SALT_PREFIX = 'thana/sync-identity/v1:'

/*
 * Test seam, matching `crypto.ts`. Deriving an identity costs about a second, and
 * a suite that derives a dozen of them to compare would spend all its time in
 * PBKDF2 for no extra assurance.
 *
 * The shipped count is still exercised for real, once, in `syncIdentity.test.ts`.
 * A suite that only ever ran a cheap count would not know whether the shipped
 * parameter is survivable on the target device — which is the one thing about it
 * that cannot be reasoned out.
 *
 * Unlike the keyring, there is no stored iteration count to fall back on: the
 * identity is derived from memory alone, so this constant IS the format. Lowering
 * it in production would not weaken new accounts, it would make every existing
 * account underivable.
 */
let activeIterations = PBKDF2_ITERATIONS

/** Test seam. Not used by application code. */
export function setSyncIterationsForTests(iterations: number): void {
  activeIterations = iterations
}

const INFO = {
  alias: 'thana/sync-alias/v1',
  seaPassword: 'thana/sea-password/v1',
  syncKey: 'thana/sync-key/v1',
} as const

function base64url(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * The exact bytes a name contributes to the derivation.
 *
 * Normalisation is a correctness requirement, not tidiness. A name typed as
 * "Ayşe" on a phone and "Ayşe" on a laptop can be different byte sequences —
 * the same grapheme composed differently — and would silently derive a different
 * identity, presenting as "my other device isn't syncing" with nothing to see.
 * NFKC folds those together. Case and surrounding space are folded for the same
 * reason: they are differences a person does not believe they typed.
 *
 * What is deliberately NOT folded: internal spacing and punctuation. "my phone"
 * and "myphone" stay distinct, because collapsing them would silently merge two
 * accounts someone meant to keep apart.
 */
export function normaliseSyncName(name: string): string {
  return name.normalize('NFKC').trim().toLowerCase()
}

/** Everything the app needs after a successful derivation. */
export interface SyncIdentity {
  /** The blinded alias. Public — this is the one value a relay sees. */
  alias: string
  /** Passed to Gun as the account password. Never the user's passphrase. */
  seaPassword: string
  /** Seals records and, via `deriveSoulKey`, names souls. Never leaves the device. */
  syncKeyMaterial: Uint8Array
}

async function hkdf(master: CryptoKey, info: string, bytes: number): Promise<Uint8Array> {
  const derived = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      // No salt: the PBKDF2 pass upstream already carries the per-user salt, and
      // HKDF's salt is not where this design's separation comes from — `info` is.
      salt: new Uint8Array(0) as unknown as BufferSource,
      info: new TextEncoder().encode(info),
    },
    master,
    bytes * 8,
  )
  return new Uint8Array(derived)
}

/**
 * Derive the identity. Deterministic: same inputs, same outputs, on any device.
 *
 * Slow on purpose — 600,000 PBKDF2 iterations, the same cost the PIN pays in
 * `crypto.ts`. This runs when sync is enabled and when a second device signs in,
 * not on any screen someone waits behind repeatedly.
 */
export async function deriveSyncIdentity(
  syncName: string,
  passphrase: string,
): Promise<SyncIdentity> {
  const name = normaliseSyncName(syncName)
  if (name === '') throw new Error('A sync name is required')
  if (passphrase === '') throw new Error('A sync passphrase is required')

  /*
   * The name is the salt.
   *
   * Not random, because a random salt would have to be stored somewhere both
   * devices can read — which means on the relay, which means an attacker gets it
   * too, and a second device could not derive anything from memory alone.
   *
   * A fixed salt was the alternative and is worse: it makes one precomputed table
   * work against every Thana user at once. Per-user salting forces an attacker to
   * spend the 600,000 iterations again for each account they want.
   *
   * The prefix is domain separation, so this derivation can never collide with
   * another that also salts by a user-chosen string.
   */
  const salt = new TextEncoder().encode(SALT_PREFIX + name)

  const passKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase) as unknown as BufferSource,
    'PBKDF2',
    false,
    ['deriveBits'],
  )

  const masterBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt as unknown as BufferSource,
      iterations: activeIterations,
      hash: 'SHA-256',
    },
    passKey,
    256,
  )

  const master = await crypto.subtle.importKey(
    'raw',
    masterBits,
    'HKDF',
    false,
    ['deriveBits'],
  )

  const [aliasBytes, seaBytes, syncKeyMaterial] = await Promise.all([
    hkdf(master, INFO.alias, 32),
    hkdf(master, INFO.seaPassword, 32),
    hkdf(master, INFO.syncKey, 32),
  ])

  return {
    alias: base64url(aliasBytes),
    seaPassword: base64url(seaBytes),
    syncKeyMaterial,
  }
}

/**
 * The key that seals records for sync.
 *
 * Separate from the DEK, which seals records on THIS device, and separate from
 * the soul-naming key derived by `deriveSoulKey` from the same material.
 *
 * Not the DEK because the DEK never leaves the device and has no reason to be
 * reachable from a passphrase — binding them would mean a compromised sync
 * passphrase decrypts the local database too.
 *
 * Not the naming key because naming and sealing are different purposes, and a key
 * used for two things is a key whose compromise costs twice. `NAMESPACE-MAP.md`
 * states this as a rule; this is the sealing half of it.
 */
export async function deriveSyncSealingKey(syncKeyMaterial: Uint8Array): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey(
    'raw',
    syncKeyMaterial as unknown as BufferSource,
    'HKDF',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0) as unknown as BufferSource,
      info: new TextEncoder().encode('thana/sync-sealing/v1'),
    },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

/**
 * Recover the raw bytes of a blinded alias, for tests and diagnostics.
 *
 * Not a reversal — the alias is a KDF output and cannot be turned back into a
 * name. Only the base64url decoding, so a test can assert the alias is 32 bytes
 * of derived material rather than anything structured.
 */
export function aliasBytes(alias: string): Uint8Array {
  const padded = alias.replace(/-/g, '+').replace(/_/g, '/')
  return fromBase64(padded + '='.repeat((4 - (padded.length % 4)) % 4))
}
